import { Renderer } from './renderer/Renderer';
import { GameScene } from './renderer/scenes/GameScene';
import { UIManager } from './ui/UIManager';
import { ToastManager } from './ui/ToastManager';
import { TutorialManager } from './ui/TutorialManager';
import { GameStore } from './store/GameStore';
import { SeededRandom } from './core/rng/SeededRandom';
import { loadTheme } from './core/data/ThemeLoader';
import { createInitialState } from './core/simulation/SimulationState';
import { createGameLoop } from './core/simulation/createGameLoop';
import { resetStaffIdCounter } from './core/entities/StaffFactory';
import { resetProjectIdCounter } from './core/entities/ProjectFactory';
import { resetTeamIdCounter } from './core/entities/TeamFactory';
import { resetContractIdCounter } from './core/entities/ContractFactory';
import { loadStrings } from './i18n/i18n';
import { showNewGameScreen, type NewGameResult } from './ui/screens/NewGameScreen';
import { showTitleScreen } from './ui/screens/TitleScreen';
import { showGameOverScreen, showCampaignEndScreen } from './ui/screens/GameOverScreen';
import { openCreateTeamDialog } from './ui/dialogs/CreateTeamDialog';
import { openStartProjectDialog } from './ui/dialogs/StartProjectDialog';
import { openHireStaffDialog } from './ui/dialogs/HireStaffDialog';
import { openDemandDialog } from './ui/dialogs/DemandDialog';
import { openSaveLoadDialog, openLoadFromTitle, autosave } from './ui/dialogs/SaveLoadDialog';
import { syncAllCounters } from './core/entities/syncCounters';
import { openOfficeUpgradeDialog } from './ui/dialogs/OfficeUpgradeDialog';
import { openTrainStaffDialog } from './ui/dialogs/TrainStaffDialog';
import { openContractsDialog } from './ui/dialogs/ContractsDialog';
import { openTeamManageDialog } from './ui/dialogs/TeamManageDialog';
import { openLoanDialog } from './ui/dialogs/LoanDialog';
import { openEquipmentDialog } from './ui/dialogs/EquipmentDialog';
import { openShipProjectDialog } from './ui/dialogs/ShipProjectDialog';
import { openProjectDetailDialog } from './ui/dialogs/ProjectDetailDialog';
import { openSettingsDialog, initUIScale } from './ui/screens/SettingsScreen';
import { openDebugMenu } from './ui/screens/DebugMenu';
import { AudioManager } from './audio/AudioManager';
import { showPauseMenu } from './ui/screens/PauseMenu';
import { openReviewRevealDialog } from './ui/dialogs/ReviewRevealDialog';
import { openRandomEventDialog } from './ui/dialogs/RandomEventDialog';
import { StaffListPanel } from './ui/panels/StaffListPanel';
import { NotificationLog } from './ui/panels/NotificationLog';
import { AchievementsPanel } from './ui/panels/AchievementsPanel';
import { StatisticsPanel } from './ui/panels/StatisticsPanel';
import { CompanyPanel } from './ui/panels/CompanyPanel';
import type { GameSpeed } from './core/types';
import type { HUDAction } from './ui/hud/HUD';
import './ui/styles.css';

import config from './data/themes/game-studio/config.json';
import entities from './data/themes/game-studio/entities.json';
import balance from './data/themes/game-studio/balance.json';
import names from './data/themes/game-studio/names.json';
import strings from './data/themes/game-studio/strings.json';

const BASE_TICK_RATE = 1.0;

let renderer: Renderer;
let uiOverlay: HTMLElement;
let canvasContainer: HTMLElement;
let theme: ReturnType<typeof loadTheme>;

async function main() {
  initUIScale();

  canvasContainer = document.getElementById('game-canvas')!;
  uiOverlay = document.getElementById('ui-overlay')!;
  if (!canvasContainer || !uiOverlay) {
    console.error('Missing #game-canvas or #ui-overlay');
    return;
  }

  theme = loadTheme(config, entities, balance, names, strings);
  loadStrings(theme.strings);

  renderer = new Renderer();
  await renderer.init(canvasContainer);

  // Hide loading screen
  const loadingEl = document.getElementById('loading');
  if (loadingEl) {
    loadingEl.classList.add('fade-out');
    setTimeout(() => loadingEl.remove(), 500);
  }

  showTitleOrNewGame();
}

function showTitleOrNewGame() {
  showTitleScreen(
    document.body,
    () => {
      // New Game → show character creation
      showNewGameScreen(document.body, (result) => startGame(result));
    },
    () => {
      // Load Game → show standalone load dialog, then start game with loaded data
      openLoadFromTitle(
        document.body,
        (data) => {
          startGame({
            companyName: data.core.company.name,
            ceoName: data.core.staff[0]?.name ?? 'Player',
            appearance: data.core.staff[0]?.appearance ?? { bodyType: 0, skinTone: 0, hairStyle: 0, hairColor: 0, outfit: 0, outfitColor: 0, accessory: 0 },
            bonusStats: {},
            difficulty: data.core.company.isHardMode ? 'hard' : 'normal',
          });
          // Replace the fresh state with the loaded one
          const newRng = new SeededRandom(0);
          newRng.setState(data.rngState);
          gameStore!.replaceState(data.core, newRng);
          syncAllCounters(data.core);
        },
        () => {
          // Cancelled — go back to title
          showTitleOrNewGame();
        },
      );
    },
  );
}

// Keep a reference for the load-from-title flow
let gameStore: GameStore | null = null;

function startGame(
  newGameResult: NewGameResult,
  prestigeBonuses?: import('./core/types').PrestigeBonuses,
  carriedAchievements?: string[],
): void {
  // Clear any previous game UI
  uiOverlay.innerHTML = '';
  document.getElementById('game-canvas')?.classList.remove('ready');

  // Create simulation
  resetStaffIdCounter();
  resetProjectIdCounter();
  resetTeamIdCounter();
  resetContractIdCounter();

  const rng = new SeededRandom(Date.now());
  const state = createInitialState(newGameResult.companyName, {
    name: newGameResult.ceoName,
    appearance: newGameResult.appearance,
    bonusStats: newGameResult.bonusStats,
  }, theme, rng, prestigeBonuses, newGameResult.difficulty === 'hard');

  // Easy mode: boost starting funds
  if (newGameResult.difficulty === 'easy') {
    state.company.funds = Math.round(state.company.funds * 1.5);
  }

  // Carry over achievements from previous prestige runs
  if (carriedAchievements && carriedAchievements.length > 0) {
    state.achievements = [...carriedAchievements];
  }

  const gameLoop = createGameLoop();
  const store = new GameStore(state, theme, rng, gameLoop, true);
  gameStore = store;

  // Speed control
  const setSpeed = (speed: GameSpeed) => {
    store.dispatch({ type: 'SET_SPEED', speed });
  };

  // Game scene
  // Clear old stage children
  while (renderer.stage.children.length > 0) {
    renderer.stage.removeChildAt(0);
  }
  const gameScene = new GameScene(renderer, store);

  // Audio
  const audio = new AudioManager(store);
  audio.start(); // User already clicked "Start Game" so we can init audio

  // Side panels
  const staffPanel = new StaffListPanel(uiOverlay, store);
  const companyPanel = new CompanyPanel(uiOverlay, store);
  const achievementsPanel = new AchievementsPanel(uiOverlay, store);
  const statisticsPanel = new StatisticsPanel(uiOverlay, store);
  // Panel backdrop — closes panels when tapping outside
  const panelBackdrop = document.createElement('div');
  panelBackdrop.className = 'panel-backdrop';
  panelBackdrop.style.display = 'none';
  panelBackdrop.addEventListener('click', () => hideAllPanels());
  uiOverlay.appendChild(panelBackdrop);

  const hideAllPanels = () => {
    staffPanel.hide(); companyPanel.hide(); notifLog.hide(); achievementsPanel.hide(); statisticsPanel.hide();
    panelBackdrop.style.display = 'none';
  };
  const showPanel = (panel: { toggle: () => void; isVisible: boolean }) => {
    hideAllPanels();
    panel.toggle();
    panelBackdrop.style.display = panel.isVisible ? '' : 'none';
  };

  // HUD action handler
  const onAction = (action: HUDAction) => {
    if (action === 'createTeam') openCreateTeamDialog(uiOverlay, store, () => {});
    else if (action === 'startProject') openStartProjectDialog(uiOverlay, store, () => {});
    else if (action === 'hire') openHireStaffDialog(uiOverlay, store, () => {});
    else if (action === 'save') openSaveLoadDialog(uiOverlay, store, 'save', () => {});
    else if (action === 'load') openSaveLoadDialog(uiOverlay, store, 'load', () => {});
    else if (action === 'staffList') { showPanel(staffPanel); }
    else if (action === 'company') { showPanel(companyPanel); }
    else if (action === 'upgradeOffice') openOfficeUpgradeDialog(uiOverlay, store, () => {});
    else if (action === 'train') openTrainStaffDialog(uiOverlay, store, () => {});
    else if (action === 'contracts') openContractsDialog(uiOverlay, store, () => {});
    else if (action === 'teams') openTeamManageDialog(uiOverlay, store, () => {});
    else if (action === 'loans') openLoanDialog(uiOverlay, store, () => {});
    else if (action === 'equipment') openEquipmentDialog(uiOverlay, store, () => {});
    else if (action === 'log') { showPanel(notifLog); }
    else if (action === 'achievements') { showPanel(achievementsPanel); }
    else if (action === 'statistics') { showPanel(statisticsPanel); }
  };

  const ui = new UIManager(uiOverlay, setSpeed, onAction);

  // Click project in HUD → open detail dialog
  ui.onProjectClick = (projectId: string) => {
    openProjectDetailDialog(uiOverlay, store, projectId, () => {});
  };

  // Ship project from HUD button (fallback if ship dialog was dismissed)
  ui.onShipProject = (projectId: string) => {
    openShipProjectDialog(uiOverlay, store, projectId, () => {});
  };

  // Toast + tutorial + notification log
  const toastMgr = new ToastManager(uiOverlay, store);
  const tutorialMgr = new TutorialManager(uiOverlay, store);
  const notifLog = new NotificationLog(uiOverlay, store);

  // Auto-open demand dialogs
  store.on('STAFF_DEMAND', (event) => {
    openDemandDialog(uiOverlay, store, event, () => {});
  });

  // Timeline tracking for campaign end screen
  const addTimeline = (text: string) => {
    const s = store.getState();
    if (!s.timeline) (s as { timeline: { year: number; week: number; text: string }[] }).timeline = [];
    if (s.timeline.length < 100) {
      s.timeline.push({ year: s.company.currentYear, week: s.company.currentWeek, text });
    }
  };
  store.on('PRODUCT_RELEASED', (e) => { if (e.type === 'PRODUCT_RELEASED') addTimeline(`Released "${e.name}"`); });
  store.on('REVIEW_SCORES_GENERATED', (e) => { if (e.type === 'REVIEW_SCORES_GENERATED' && e.isHallOfFame) addTimeline(`"${e.name}" entered Hall of Fame!`); });
  store.on('OFFICE_UPGRADED', (e) => { if (e.type === 'OFFICE_UPGRADED') addTimeline(`Upgraded to ${e.newName}`); });
  store.on('STAFF_HIRED', (e) => { if (e.type === 'STAFF_HIRED') addTimeline(`Hired ${e.name}`); });
  store.on('PLATFORM_LAUNCHED', (e) => { if (e.type === 'PLATFORM_LAUNCHED') addTimeline(`${e.platformName} launched`); });
  store.on('ACHIEVEMENT_UNLOCKED', (e) => { if (e.type === 'ACHIEVEMENT_UNLOCKED') addTimeline(`${e.icon} ${e.name}`); });

  // Track firing for achievement
  store.on('STAFF_FIRED', () => {
    const s = store.getState();
    if (!s.tutorialTipsShown.includes('achievement_fired')) {
      (s.tutorialTipsShown as string[]).push('achievement_fired');
    }
  });

  // Random/scripted events → show dialog
  store.on('RANDOM_EVENT', (event) => {
    openRandomEventDialog(uiOverlay, store, event, () => {});
  });
  store.on('SCRIPTED_EVENT', (event) => {
    openRandomEventDialog(uiOverlay, store, event, () => {});
  });

  // Review reveal + celebration
  store.on('REVIEW_SCORES_GENERATED', (event) => {
    openReviewRevealDialog(uiOverlay, store, event, () => {});
    if (event.type === 'REVIEW_SCORES_GENERATED' && event.isHallOfFame) {
      gameScene.celebrate('★ HALL OF FAME ★');
    }
  });

  // Celebrate company level ups
  store.on('COMPANY_LEVELED_UP', (event) => {
    if (event.type === 'COMPANY_LEVELED_UP') {
      gameScene.celebrate(`Level ${event.newLevel}!`);
    }
  });

  // Ship decision
  store.on('PROJECT_READY_TO_SHIP', (event) => {
    if (event.type === 'PROJECT_READY_TO_SHIP') {
      openShipProjectDialog(uiOverlay, store, event.projectId, () => {});
    }
  });

  // Game Over
  store.on('GAME_OVER', () => {
    setSpeed(0);
    setTimeout(() => {
      showGameOverScreen(document.body, store.getState(), () => {
        cleanup();
        showTitleOrNewGame();
      });
    }, 500);
  });

  // Campaign Complete
  store.on('CAMPAIGN_COMPLETED', () => {
    setSpeed(0);
    setTimeout(() => {
      const finalState = store.getState();
      showCampaignEndScreen(
        document.body,
        finalState,
        () => {
          // New Game+ — compute prestige bonuses from completed campaign
          const prevPrestige = finalState.company.prestigeLevel ?? 0;
          const newPrestigeLevel = prevPrestige + 1;
          const prestige = theme.balance.prestige;
          const keepUnlocks = Math.min(newPrestigeLevel * prestige.unlocksToKeepPerLevel, finalState.unlocks.length);
          const keptUnlocks = finalState.unlocks.slice(0, keepUnlocks);
          const keptAchievements = [...(finalState.achievements ?? [])];

          cleanup();
          // Go to new game screen with prestige bonuses pre-set
          showNewGameScreen(document.body, (result) => {
            const prestigeBonuses = {
              startingFunds: Math.min(newPrestigeLevel * prestige.startingFundsPerLevel, prestige.startingFundsCap),
              xpMultiplier: Math.min(1 + newPrestigeLevel * prestige.xpMultiplierPerLevel, 1 + prestige.xpMultiplierCap),
              reputationBonus: Math.min(newPrestigeLevel * 5, 20),
              unlockedFromStart: keptUnlocks,
            };
            startGame(result, prestigeBonuses, keptAchievements);
          });
        },
        () => {
          // Continue playing (endless mode)
          setSpeed(1);
        },
      );
    }, 500);
  });

  // Tick loop
  let tickAccum = 0;
  let lastAutosaveWeek = 0;
  const autosaveInterval = theme.balance.autosaveIntervalWeeks;

  const tickCallback = (delta: number) => {
    const speed = store.getSpeed();

    // Always update UI (even when paused — so pause button shows active)
    ui.update(store.getState());

    if (speed === 0) return;

    tickAccum += (delta / 60) * speed * BASE_TICK_RATE;
    const maxTicks = 10;
    let ticks = 0;
    while (tickAccum >= 1 && ticks < maxTicks) {
      tickAccum -= 1;
      ticks++;
      store.tick();
    }
    if (tickAccum > maxTicks) tickAccum = 0;

    const s = store.getState();

    // Refresh panels and audio mood
    if (ticks > 0) {
      staffPanel.render();
      companyPanel.render();

      audio.updateMood(s);
    }

    // Autosave
    const totalWeeks = Math.floor(s.company.currentTick / (theme.balance.ticksPerDay * theme.balance.daysPerWeek));
    if (totalWeeks > 0 && totalWeeks !== lastAutosaveWeek && totalWeeks % autosaveInterval === 0) {
      lastAutosaveWeek = totalWeeks;
      autosave(store);
    }
  };

  renderer.addTickerCallback(tickCallback);

  // Keyboard
  const keyHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (staffPanel.isVisible || companyPanel.isVisible || notifLog.isVisible || achievementsPanel.isVisible || statisticsPanel.isVisible) { hideAllPanels(); return; }
      if (!ui.handleBack()) {
        // Show pause menu
        showPauseMenu(
          document.body,
          store,
          audio,
          () => setSpeed(1), // resume
          () => openSaveLoadDialog(uiOverlay, store, 'save', () => {}),
          () => openSaveLoadDialog(uiOverlay, store, 'load', () => {}),
          () => { cleanup(); showTitleOrNewGame(); },
          () => openSettingsDialog(uiOverlay, audio, () => {}),
        );
      }
    }
    // Ignore keyboard shortcuts when typing in an input/textarea
    const tag = (document.activeElement?.tagName ?? '').toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

    if (e.key === ' ') { e.preventDefault(); setSpeed(store.getSpeed() === 0 ? 1 : 0); }
    if (e.key === '1') setSpeed(1);
    if (e.key === '2') setSpeed(2);
    if (e.key === '3') setSpeed(4);
    if (e.key === 'm' || e.key === 'M') audio.toggleMute();
    if (e.key === 'F9') openDebugMenu(uiOverlay, store, () => {});
  };
  document.addEventListener('keydown', keyHandler);

  // Cleanup function for restarting
  function cleanup() {
    // Remove ticker callback — can't easily remove, so we'll just clear UI
    gameScene.destroy();
    ui.destroy();
    staffPanel.destroy();
    companyPanel.destroy();
    toastMgr.destroy();
    tutorialMgr.destroy();
    notifLog.destroy();
    achievementsPanel.destroy();
    statisticsPanel.destroy();
    audio.destroy();
    document.removeEventListener('keydown', keyHandler);
    uiOverlay.innerHTML = '';
  }
}

main().catch(console.error);

// Capacitor lifecycle — pause audio when app goes to background
async function setupCapacitorLifecycle() {
  try {
    const { App } = await import('@capacitor/app');
    App.addListener('pause', () => {
      // Pause game and audio when app goes to background
      if (gameStore) {
        gameStore.dispatch({ type: 'SET_SPEED', speed: 0 });
      }
    });
    App.addListener('backButton', () => {
      // Android back button — treat as Escape
      document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
    });
  } catch {
    // Not running in Capacitor — ignore
  }
}
setupCapacitorLifecycle();
